/**
 * Profile Management - Redesigned
 * Modern profile management interface for admin users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../components/common/NotificationSystem';
import { authService } from '../../services/authService';
import ModernCard from '../../components/common/ModernCard';
import { 
  ModernForm, 
  FormField, 
  ModernInput, 
  ModernButton, 
  FormRow 
} from '../../components/common/ModernForm';
import ProfessionalLoader from "../../components/common/ProfessionalLoader";
import './ProfileManagement.css';

const ProfileManagement = ({ userRole }) => {
  const { user, updateUser } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'Australia'
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zip_code: user.zip_code || '',
        country: user.country || 'Australia'
      });
    }
  }, [user]);

  const loadProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await authService.getProfile();
      
      if (response.success) {
        const profileData = response.data;
        setFormData({
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
          email: profileData.email || '',
          phone: profileData.phone || '',
          address: profileData.address || '',
          city: profileData.city || '',
          state: profileData.state || '',
          zip_code: profileData.zip_code || '',
          country: profileData.country || 'Australia'
        });
      }
    } catch (error) {
      showError('Failed to load profile data', 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };


  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsSaving(true);
      const response = await authService.updateProfile(formData);
      
      if (response.success) {
        showSuccess('Profile updated successfully', 'Success');
        updateUser(response.data);
      } else {
        showError(response.message || 'Failed to update profile', 'Error');
      }
    } catch (error) {
      showError('Failed to update profile. Please try again.', 'Error');
    } finally {
      setIsSaving(false);
    }
  };



  if (isLoading) {
    return (
      <div className="profile-management">
        <div className="profile-loading">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  const renderProfileTab = () => (
    <ModernCard variant="elevated" padding="xl">
      <div className="profile-card__header">
        <h2 className="profile-card__title">Profile Information</h2>
        <p className="profile-card__subtitle">
          Update your personal information and contact details
        </p>
      </div>
      
      <ModernForm onSubmit={handleSaveProfile}>
        <FormRow>
          <FormField 
            label="First Name" 
            required 
            error={errors.first_name}
          >
            <ModernInput
              value={formData.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              placeholder="Enter your first name"
              error={!!errors.first_name}
            />
          </FormField>
          
          <FormField 
            label="Last Name" 
            required 
            error={errors.last_name}
          >
            <ModernInput
              value={formData.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              placeholder="Enter your last name"
              error={!!errors.last_name}
            />
          </FormField>
        </FormRow>
        
        <FormField 
          label="Email Address" 
          required 
          error={errors.email}
          hint="This will be used for login and notifications"
        >
          <ModernInput
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="Enter your email address"
            error={!!errors.email}
          />
        </FormField>
        
        <FormField 
          label="Phone Number" 
          error={errors.phone}
        >
          <ModernInput
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="Enter your phone number"
            error={!!errors.phone}
          />
        </FormField>
        
        <FormField 
          label="Address" 
          error={errors.address}
        >
          <ModernInput
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            placeholder="Enter your street address"
            error={!!errors.address}
          />
        </FormField>
        
        <FormRow>
          <FormField 
            label="City" 
            error={errors.city}
          >
            <ModernInput
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="Enter your city"
              error={!!errors.city}
            />
          </FormField>
          
          <FormField 
            label="State" 
            error={errors.state}
          >
            <ModernInput
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              placeholder="Enter your state"
              error={!!errors.state}
            />
          </FormField>
          
          <FormField 
            label="ZIP Code" 
            error={errors.zip_code}
          >
            <ModernInput
              value={formData.zip_code}
              onChange={(e) => handleInputChange('zip_code', e.target.value)}
              placeholder="Enter your ZIP code"
              error={!!errors.zip_code}
            />
          </FormField>
        </FormRow>
        
        <FormField 
          label="Country" 
          error={errors.country}
        >
          <ModernInput
            value={formData.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
            placeholder="Enter your country"
            error={!!errors.country}
          />
        </FormField>
        
        <div className="profile-card__actions">
          <ModernButton
            type="submit"
            variant="primary"
            size="lg"
            loading={isSaving}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </ModernButton>
        </div>
      </ModernForm>
    </ModernCard>
  );


  const renderPreferencesTab = () => (
    <ModernCard variant="elevated" padding="xl">
      <div className="profile-card__header">
        <h2 className="profile-card__title">Preferences</h2>
        <p className="profile-card__subtitle">
          Customize your application preferences
        </p>
      </div>
      
      <div className="preferences-content">
        <div className="preference-item">
          <div className="preference-info">
            <h3 className="preference-title">Email Notifications</h3>
            <p className="preference-description">
              Receive email notifications for important updates
            </p>
          </div>
          <div className="preference-control">
            <label className="preference-toggle">
              <input type="checkbox" defaultChecked />
              <span className="preference-toggle__slider"></span>
            </label>
          </div>
        </div>
        
        <div className="preference-item">
          <div className="preference-info">
            <h3 className="preference-title">Dark Mode</h3>
            <p className="preference-description">
              Switch to dark mode for better viewing in low light
            </p>
          </div>
          <div className="preference-control">
            <label className="preference-toggle">
              <input type="checkbox" />
              <span className="preference-toggle__slider"></span>
            </label>
          </div>
        </div>
        
        <div className="preference-item">
          <div className="preference-info">
            <h3 className="preference-title">Auto-save</h3>
            <p className="preference-description">
              Automatically save changes as you type
            </p>
          </div>
          <div className="preference-control">
            <label className="preference-toggle">
              <input type="checkbox" defaultChecked />
              <span className="preference-toggle__slider"></span>
            </label>
          </div>
        </div>
      </div>
    </ModernCard>
  );


  return (
    <div className="profile-management">
      <div className="profile-header">
        <div className="profile-header__content">
          <div className="profile-avatar">
            <span className="profile-avatar__text">
              {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'A'}
            </span>
          </div>
          <div className="profile-info">
            <h1 className="profile-name">
              {user?.first_name && user?.last_name 
                ? `${user.first_name} ${user.last_name}`
                : user?.email || 'Profile'
              }
            </h1>
            <p className="profile-role">{userRole || 'Admin'}</p>
            <p className="profile-email">{user?.email}</p>
          </div>
        </div>
      </div>

      <div className="profile-content">
        {renderProfileTab()}
        {renderPreferencesTab()}
      </div>
    </div>
  );
};

export default ProfileManagement;